import { useState, useEffect } from 'react'
import { Terminal, Bot, Loader2 } from 'lucide-react'
import TerminalPanel from './TerminalPanel'
import { listAppSessions } from '../../lib/api'

interface TerminalHostProps {
  appName: string
  initialSession?: string
  onSessionChange?: (session: string) => void
  onSendDataRef?: React.MutableRefObject<((data: string) => void) | null>  // Ref for external data injection (MobileKeyboardBar)
  requestedSession?: string  // External session switch request (work-area dropdown, GitPanel, panel handoff)
  onSwitchToClaude?: () => void  // Switch the main pane to the Claude panel
}

/**
 * Hosts the terminal for the active work area. Replaced the old TerminalTabs
 * multi-tab bar: work areas are now created/switched/deleted exclusively from
 * the work-area (Layers) dropdown in the workspace header, so the terminal
 * just attaches to whatever work area the parent says is active.
 *
 * This component deliberately NEVER creates a work area. The old tab flow
 * auto-created `tab-N` names (and could resurrect deleted work areas from
 * stale state); creation now has exactly one path — the named-create modal.
 * A requested work area that no longer exists resolves to `main` instead.
 */
export default function TerminalHost({
  appName,
  initialSession = 'main',
  onSessionChange,
  onSendDataRef,
  requestedSession,
  onSwitchToClaude,
}: TerminalHostProps) {
  const [session, setSession] = useState(initialSession)
  const [switching, setSwitching] = useState(false)

  // Follow external switch requests, validating that the target still exists.
  useEffect(() => {
    const target = requestedSession
    if (!target || target === session) return
    let cancelled = false
    setSwitching(true)
    ;(async () => {
      let resolved = target
      try {
        const known = await listAppSessions(appName)
        const exists = known.sessions.some(s => s.name === target && s.status !== 'archived')
        if (!exists) resolved = 'main'
      } catch {
        // Can't verify — attach as requested rather than silently rerouting.
      }
      if (!cancelled) {
        setSession(resolved)
        setSwitching(false)
      }
    })()
    return () => { cancelled = true }
  }, [requestedSession, session, appName])

  // Keep the parent's activeSession in step (it drives preview URL + panels),
  // including the fall-back-to-main correction above.
  useEffect(() => {
    onSessionChange?.(session)
  }, [session, onSessionChange])

  return (
    <div className="flex flex-col h-full">
      {/* Slim header: current work area + switch-to-Claude */}
      <div className="flex items-center border-b border-border bg-background h-7 flex-shrink-0">
        <div className="flex items-center gap-1.5 px-2 h-full text-xs text-muted-foreground">
          <Terminal className="w-3 h-3" />
          <span className="truncate max-w-[160px]">{session}</span>
          {switching && <Loader2 className="w-3 h-3 animate-spin" />}
        </div>
        {onSwitchToClaude && (
          <button title="Switch to the Claude panel"
            onClick={onSwitchToClaude}
            className="flex items-center gap-1 px-2 h-full text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
          >
            <Bot className="w-3.5 h-3.5" />
            <span>Claude</span>
          </button>
        )}
      </div>

      {/* Terminal — remounts when the work area changes */}
      <div className="flex-1 relative min-h-0">
        <div key={session} className="absolute inset-0">
          <TerminalPanel
            appName={appName}
            tmuxSession={session}
            closeable={false}
            onSendDataRef={onSendDataRef}
          />
        </div>
      </div>
    </div>
  )
}
